import { Fragment, useEffect, useMemo, useState } from 'react';
import { Feature } from '../../../types/catalog';
import FileAccordion from './elements/file-accordion';
import SectionAccordion from './elements/section-accordion';
import FeatureTable from './elements/feature-table';
import LogisticFeatureTable from './elements/logistic-feature-table';
import ProductCarousel from './elements/product-carousel';
import FeatureCarousel from './elements/features-carousel';
import SwiperGallery from './elements/swiper-gallery';
import Product, { ProductRenderProps } from '../../product';
import NewIekNotFound from './not-found';
import QRCodePopup from './elements/qr-code-popup';
import VideoDisplay from './elements/video';

const navLinks = {
    description: 'Описание',
    advantages: 'Преимущества',
    files: 'Документация',
    specs: 'Технические характеристики',
    usage: 'Эксплуатационные параметры',
    logistics: 'Логистические параметры',
    software: 'Программное обеспечение',
    complects: 'Комплектация',
    complectations: 'Комплектация',
    designFeatures: 'Особенности конструкции',
    analogs: 'Аналоги',
    related: 'Совместно применяемые изделия',
};
type showKey = keyof typeof navLinks;
type ShowProps = {
    [key in showKey]: any;
};

const NavLinks = ({ show }: { show: ShowProps }) => {
    const links = useMemo(
        () =>
            Object.entries(navLinks)
                .map(([id, name]) => ({
                    id,
                    name,
                }))
                .filter(({ id }) => show[id as showKey]),
        [show],
    );
    const [visible, setVisible] = useState<Record<string, boolean>>({});
    useEffect(() => {
        if (!IntersectionObserver) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                setVisible((visible) => {
                    const newV = { ...visible };
                    for (const e of entries) {
                        newV[e.target.id] = e.isIntersecting;
                    }
                    return newV;
                });
            },
            { threshold: [0.3] },
        );
        for (const link of links) {
            const el = document.getElementById(link.id);
            if (el) {
                observer.observe(el);
            }
        }
        return () => observer.disconnect();
    }, [links]);
    return (
        <div className='product-nav'>
            {links.map(({ id, name }) => (
                <div key={id} className='product-nav-item'>
                    <a
                        href={`#${id}`}
                        className={
                            'product-nav-item__link' + (visible[id] ? ' product-nav-item__link--active' : '')
                        }
                    >
                        {name}
                    </a>
                </div>
            ))}
        </div>
    );
};

const ProductRender = ({ show, product }: ProductRenderProps) => {
    const [showQR, setShowQR] = useState(false);
    if (!product) {
        return <NewIekNotFound>Товар не найден</NewIekNotFound>;
    }
    return (
        <Fragment key={product.article}>
            <section className='product-info'>
                <div className='wrap'>
                    {product.novelty && <span className='iek-prodcat-catalog-detail-novelty'>Новинка</span>}
                    <div className='product-info__content'>
                        <div className='product-info__left'>
                            <SwiperGallery images={product.imageUrls} variants={product.imageUrlsVariants} />
                        </div>
                        <div className='product-info__right' id='description'>
                            <div className='product-info__code'>Артикул: {product.article}</div>
                            <div className='product-info-description'>
                                {product.description?.split('\n').map((t, idx) => <p key={idx}>{t}</p>) ||
                                'Описание отсутствует'}
                            </div>
                            <div className='product-info__buttons'>
                                {/*<div className="ui-button ui-button--outline">Калькулятор подбора</div>*/}
                                <a
                                    href={'/products/where_to_buy/?article=' + product.article}
                                    className='ui-button'
                                >
                                    ГДЕ КУПИТЬ?
                                </a>
                                <div className='ui-button ui-button--outline' onClick={() => setShowQR(true)}>
                                    СГЕНЕРИРОВАТЬ QR-КОД
                                </div>
                                {showQR && <QRCodePopup article={product.article} name={product.name}
                                                        close={() => setShowQR(false)} />}
                            </div>
                        </div>
                    </div>
                </div>
                <NavLinks show={show} />
            </section>
            <section className='product-description'>
                <div className='wrap'>
                    <div className='product-description__content'>
                        <div className='product-description__left text-content'>
                            {show.advantages && (
                                <div className='product-description-advantages' id='advantages'>
                                    <ul>
                                        {product.advantages?.split('\n').map((t, idx) => (
                                            <li key={idx}>{t}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {show.videos && product.videos.map((video) => (
                                <VideoDisplay {...video}  />
                            ))}
                        </div>
                        <div className='product-description__right'>
                            {show.files && (
                                <div className='product-description__documents' id='files'>
                                    {product.files.map((group, idx) => (
                                        <FileAccordion fileGroup={group} key={idx} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
            <section className='product-parameters'>
                <div className='wrap'>
                    {show.specs && (
                        <SectionAccordion title='Технические характеристики' id='specs'>
                            <FeatureTable features={product.etim?.features as Feature[]} />
                        </SectionAccordion>
                    )}
                    {show.usage && (
                        <SectionAccordion title='Эксплуатационные параметры' id='usage'>
                            <FeatureTable features={product.leftPeriod as Feature[]} />
                        </SectionAccordion>
                    )}
                    {show.logistics && (
                        <SectionAccordion title='Логистические параметры' id='logistics'>
                            <LogisticFeatureTable features={product.logisticParams} />
                        </SectionAccordion>
                    )}

                    {(show.software || show.complects || show.complectations) && (
                        <div className='product-parameters__container'>
                            {show.software && (
                                <div className='product-parameters__left' id='software'>
                                    <h2>Программное обеспечение</h2>
                                    {product.software.map(({ name, description, url, size }, idx) => (
                                        <div key={idx} className='product-parameters-software'>
                                            <div className='product-parameters-software__image' />
                                            <div className='product-parameters-software__group'>
                                                <div className='product-parameters-software__title'>{name}</div>
                                                <p className='product-parameters-software__text'>{description}</p>
                                                <a href={url} className='product-parameters-software__link'>
                                                    Скачать ({Number(size / 1_000_000).toLocaleString()} МБ)
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {show.complects && (
                                <div className='product-parameters__right' id='complects'>
                                    <div className='product-parameters-item'>
                                        <h2 className='product-parameters-item__title'>Комплектация</h2>
                                        <FeatureTable features={product.complects} />
                                    </div>
                                </div>
                            )}
                            {show.complectations && (
                                <div className='product-parameters__right' id='complectations'>
                                    <div className='product-parameters-item'>
                                        <h2 className='product-parameters-item__title'>Комплектация</h2>
                                        {product.complectations?.split('\n').map((t, idx) => <p key={idx}>{t}</p>)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
            {show.designFeatures && (
                <section className='product-features product-features--detail' id='designFeatures'>
                    <div className='wrap'>
                        <h2 className='product-features__title'>Особенности конструкции</h2>
                        <div className='product-features-slider'>
                            <FeatureCarousel features={product.designFeatures} />
                        </div>
                    </div>
                </section>
            )}

            <section className='product-analogs'>
                <div className='wrap'>
                    {show.analogs && (
                        <div className='products-slider products-slider--analogs' id='analogs'>
                            <h2 className='products-slider__title'>Аналоги</h2>
                            <div className='products-slider__carousel'>
                                <ProductCarousel products={product.analogs} swiperProps={{ slidesPerView: 4 }} />
                            </div>
                        </div>
                    )}
                    {show.related && (
                        <div className='products-slider products-slider--shared' id='related'>
                            <h2 className='products-slider__title'>Совместно применяемые изделия</h2>
                            <div className='products-slider__carousel'>
                                <ProductCarousel products={product.related} swiperProps={{ slidesPerView: 4 }} />
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </Fragment>
    );
};

export function NewIekProduct() {
    return <Product render={ProductRender} />;
}

export default NewIekProduct;
